/**
 * ESTABLISHMENT-IMPORT-REVIEW-MODAL
 * Summary of the work units, risks and actions extracted from the unique document, then import
 */
import Modal from "@/components/modal"
import ModalConfirm from "@/components/modal-confirm"
import Button from "@/components/button"
import { useAppDispatch } from "@/core/root-reducer"
import { STATUS_LOADED, STATUS_SAVING } from "@/redux/_status.types"
import { faDownload } from "@fortawesome/free-solid-svg-icons"
import CongratulationModal from "@/modals/congratulation.modal"
import { useState } from "react"
import establishmentStore from "../../model/establishment.store"
import { downloadUrl } from "@/entities/document/utils/document-file.utils"
import { ImportedDocument, RawDocument } from "../../model/duerp-mapping.utils"

//La lecture se reimporte telle quelle (fichier .json) pour retester sans rappeler l'assistant
export function RawDocumentDownloadButton({ raw }: { raw: RawDocument }) {
  return (
    <Button
      icon={faDownload}
      onClick={() =>
        downloadUrl(
          URL.createObjectURL(new Blob([JSON.stringify(raw, null, 2)], { type: "application/json" })),
          "document-unique.json"
        )
      }>
      Télécharger JSON
    </Button>
  )
}

interface Props {
  //Lecture brute, telechargeable pour rejouer l'import sans l'assistant
  raw: RawDocument
  result: ImportedDocument
  onClose: () => void
}

export default function EstablishmentImportReviewModal({ raw, result, onClose }: Props) {
  const dispatch = useAppDispatch()
  const [isConfirmCloseOpen, setIsConfirmCloseOpen] = useState(false)
  const [status, setStatus] = useState(STATUS_LOADED)
  const [isImported, setIsImported] = useState(false)

  //Les unites de travail sont importees comme fonctions
  async function handleImport() {
    setStatus(STATUS_SAVING)
    const response: any = await dispatch(
      establishmentStore.thunks.importDocumentConfirm({
        populations: result.units,
        sites: [],
        risks: result.risks.map(({ units, ...risk }) => ({ ...risk, populations: units, sites: [] }))
      })
    )
    setStatus(STATUS_LOADED)
    if (response.payload?.error) return

    dispatch(establishmentStore.thunks.fetchData())
    setIsImported(true)
  }

  const actionsCount = result.risks.reduce((count, risk) => count + risk.preventionActions.length, 0)

  if (isImported) {
    return <CongratulationModal text="Votre document unique a bien été importé." onClose={onClose} />
  }

  return (
    <>
      <Modal
        title="Importer mon document unique"
        disableClickOutside
        disableKeyEvents
        onClose={() => setIsConfirmCloseOpen(true)}
        footerStart={<RawDocumentDownloadButton raw={raw} />}
        onNext={handleImport}
        onNextCta="Importer"
        status={status}>
        <p>
          {`Vous êtes sur le point d'importer ${result.units.length} fonctions, ${result.risks.length} risques et ${actionsCount} mesures de prévention.`}
        </p>
      </Modal>

      {isConfirmCloseOpen && (
        <ModalConfirm
          textBold="Abandonner l'import ? Les données extraites du document seront perdues."
          onNo={() => setIsConfirmCloseOpen(false)}
          onYes={onClose}
        />
      )}
    </>
  )
}
